import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import {
  applyRelativeOffset,
  createWaiter,
  animDuration,
  getApplication,
  getCreativeSuffix,
  parseAmount,
} from '../../lib/utils';
import Animator from 'src/lib/Animator';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import uiConfig from 'src/lib/ui/config';
import { shouldShowHeader } from 'src/redux/getters/ui';
import * as Inventory from 'src/replicant/inventory';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
} from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import * as Pet from 'src/replicant/pet';
import StateObserver from 'src/StateObserver';
import statePromise from '../../lib/statePromise';
import sounds from 'src/lib/sounds';
import { State } from 'src/state';
import { formatDuration } from 'src/lib/formatDuration';
import { duration } from 'src/replicant/utils/duration';
import { CircularProgressBar } from './shared/CircularProgressBar';
import * as Level from 'src/replicant/level';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { HudMeter, HudMeterOpts } from './HudMeter';
import { setVisibleStats } from 'src/state/ui';
import {
  isBPointsButtonDisabled,
  isEnergyButtonDisabled,
  isGemsButtonDisabled,
  openCoinsShopSequence,
  openEnergyShopSequence,
  openGemsShopSequence,
} from '../shop/Helpers';
import { isSceneEntered } from 'src/lib/stateUtils';
import {
  getDisplayedBPoints,
  getDisplayedEnergy,
  getDisplayedGems,
} from '../inventory/inventory.util';
import { Attributes } from '../pet/pet.redux';
import { PetController } from '../pet/PetController';
import { getFriendsPet, getPet } from '../pet';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import Match2FrenzyBar from '../minigame/match2-timestep/components/Match2FrenzyBar';
import {
  getMatch2TimedRewardRemainingTime,
  isMatch2Available,
} from 'src/replicant/match2/match2.getters';
import features from 'src/replicant/ruleset/features';
import { canShowMatch2FrenzyBar } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { registerHandTarget } from 'src/game/tutorial/services/TutorialHandService';
import PopupMonitor from '../logic/PopupMonitor';
import type { PopupID } from './popups/popupRegistry';

const GEMS_UPDATE_ALLOWED_POPUPS = new Set<PopupID>([
  'popupConversationScript',
  'popupDescriptiveScript',
  'popupGiftsList',
  'popupMatch2MagicLamps',
  'popupSelectionalScript',
  'popupSpeechBubble',
  'popupTreasureExpress',
  'popupWantToAddFriend',
]);

export default class Header {
  private container: View;
  private petName!: LangBitmapFontTextView;
  private petSpecies!: LangBitmapFontTextView;
  private petAge!: LangBitmapFontTextView;
  private levelLabel!: LangBitmapFontTextView;
  private coins!: LangBitmapFontTextView;
  private coinsIcon!: ImageView;
  private gems!: LangBitmapFontTextView;
  private gemsIcon!: ImageScaleView;
  private energy!: LangBitmapFontTextView;
  private energyIcon!: ImageView;
  private loveMeter!: HudMeter;
  private stomachMeter!: HudMeter;
  private switchButton?: ButtonScaleView;
  private availableDisplays!: string[];

  private active = false;

  private coinsAnimator = new Animator((value) => {
    this.coins.localeText = () => parseAmount(value);
  });

  private gemsAnimator = new Animator((value) => {
    this.gems.localeText = () => parseAmount(value);
  });

  private energyAnimator = new Animator((value) => {
    this.energy.localeText = () =>
      `${value}/${Inventory.getters.getMaxEnergy(StateObserver.getState().user)}`;
  });
  private energyLayout!: ButtonScaleView;
  private energyRegenerationFrame!: ImageScaleView;
  private energyRegeneration!: LangBitmapFontTextView;
  private levelProgress!: CircularProgressBar;
  private friendsNamePlate?: ImageScaleView;
  private friendsNamePlateText!: LangBitmapFontTextView;

  // Match2 frenzy specific UI.
  private energyUnlimitedIcon?: ImageScaleView;
  private energyUnlimitedTimer?: LangBitmapFontTextView;
  private frenzyBar?: Match2FrenzyBar;

  constructor(private opts: { superview: View }) {
    this.active = false;

    StateObserver.dispatch(setVisibleStats(platform.playerID));
    this.availableDisplays = [platform.playerID];

    this.container = new View({
      tag: 'header',
      zIndex: 999,
      width: uiConfig.width,
      opacity: 0,
      infinite: true,
      canHandleEvents: false,
    });

    // anchor elements
    createEmitter(this.container, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.container.updateOpts({ y: screen.top });
      },
    );

    this.createNameTag();
    this.createLoveMeter();
    this.createStomachMeter();
    this.coins = this.createCoins();
    this.createGems();
    this.energy = this.createEnergy();

    const now = StateObserver.now();

    createEmitter(this.container, ({ user }) =>
      canShowMatch2FrenzyBar(user, now),
    ).addListener((enabled) => {
      if (!enabled) {
        this.frenzyBar?.updateOpts({ visible: false });
        return;
      }

      const frenzyBar = this.getMatch2FrenzyBar();
      frenzyBar.syncState();
      frenzyBar.style.visible = true;
    });

    // Store current value to detect if gems are being spent or not.
    let lastSyncedGems = getDisplayedGems();

    createEmitter(this.container, ({ inventory }) => ({
      coins: getDisplayedBPoints(),
      gems: getDisplayedGems(),
      energy: getDisplayedEnergy(),
      frozen: inventory.displayFrozen,
    })).addListener(({ coins, gems, energy, frozen }) => {
      const didConsumeGems = gems < lastSyncedGems;

      if (frozen) return;
      this.coinsAnimator.setTarget(coins);

      const hasBlockingPopupOpen = PopupMonitor.getOpenPopups().some(
        (id) => !GEMS_UPDATE_ALLOWED_POPUPS.has(id),
      );

      // Gem spends update immediately; gem gains wait for blocking popups to close.
      if (didConsumeGems || !hasBlockingPopupOpen) {
        lastSyncedGems = gems;
        this.gemsAnimator.setTarget(gems);
      }

      this.energyAnimator.setTarget(energy);
    });

    createEmitter(this.container, ({ ui }) => ui.togglePopup).addListener(
      (popupToggle) => {
        // Only sync after a popup closes.
        if (!popupToggle || popupToggle.enabled) return;

        // Frozen currency displays should keep their current visible value.
        if (StateObserver.getState().inventory.displayFrozen) return;

        const hasOtherBlockingOpenPopups = PopupMonitor.getOpenPopups().some(
          (id) => id !== popupToggle.id && !GEMS_UPDATE_ALLOWED_POPUPS.has(id),
        );

        // Allowed popups do not block gem count updates.
        if (hasOtherBlockingOpenPopups) return;

        lastSyncedGems = getDisplayedGems();
        this.gemsAnimator.setTarget(lastSyncedGems);
      },
    );

    const attibutesEmitter = createEmitter(
      this.container,
      ({ pet, level, ui: { visibleStats, isPetCrateOpening } }) => ({
        name: pet[visibleStats]?.attributes.name,
        species: pet[visibleStats]?.species,
        age: pet[visibleStats]?.attributes.age ?? 0,
        love: isPetCrateOpening
          ? 0
          : Math.ceil(pet[visibleStats]?.attributes.love ?? 0),
        stomach: isPetCrateOpening
          ? 0
          : Math.ceil(pet[visibleStats]?.attributes.stomach ?? 0),
        level: level[visibleStats]?.current ?? 0,
        xp: level[visibleStats]?.xp ?? 0,
        visibleStats,
      }),
    );

    // Level Meter.
    attibutesEmitter.addListener(({ level, xp, visibleStats }) => {
      this.levelLabel.color = this.getLabelColor(visibleStats);
      this.levelLabel.localeText = () =>
        i18n('attributes.level', { level: Level.ruleset.toDisplay(level) });
      this.levelProgress.animateProgress(
        xp / (Level.ruleset.requiredXp(level) ?? Infinity),
      );
    });

    // Top left texts.
    attibutesEmitter.addListener(({ name, species, age, visibleStats }) => {
      const { user } = StateObserver.getState();
      this.petName.localeText = () => name ?? '';
      this.petSpecies.localeText = () =>
        Pet.ruleset.evolutions[getPetSpeciesBucket(user)][species!]?.[
          `name_${getCreativeSuffix()}`
        ] ?? '';
      this.petAge.localeText = () => i18n('attributes.age', { age });

      const color = this.getLabelColor(visibleStats);
      this.petName.color = color;
      this.petSpecies.color = color;
      this.petAge.color = color;
    });

    // Love and stomach meters.
    attibutesEmitter.addListener(({ love, stomach, visibleStats }) => {
      const color = this.getLabelColor(visibleStats);
      this.loveMeter.updateLabelOpts({ color });
      this.stomachMeter.updateLabelOpts({ color });

      if (!this.areAttributesUpdating(visibleStats)) {
        const { max } = Pet.ruleset.attributeBounds;
        this.stomachMeter.setMeterValue(stomach / max);
        this.loveMeter.setMeterValue(love / max);
      }
    });

    const visitEmitter = createEmitter(this.container, ({ user, pet }) => {
      const { visitSession } = user.visit;
      const isInFriendsRoom = isSceneEntered('friendsLivingRoom');

      // Make sure there's an active session and that attributes were initialized.
      if (visitSession?.friendId && pet[visitSession.friendId]?.attributes) {
        // Return friendId and name if there's a visit session.
        return {
          friendId: visitSession.friendId,
          friendName: visitSession.name,
          isInFriendsRoom,
        };
      }

      // Return isFriendsRoom if there's no active visit session.
      return {
        isInFriendsRoom,
      };
    });

    visitEmitter.addListener(({ friendId, friendName, isInFriendsRoom }) => {
      if (!friendId) {
        if (getApplication().isInit) {
          this.setFocusedPet(getPet());
        }
        this.switchButton?.hide();
        this.friendsNamePlate?.hide();
        attibutesEmitter.force();
        return;
      }
      this.availableDisplays = [platform.playerID, friendId];

      const switchButton = this.getSwitchButton();
      switchButton.show();
      switchButton.onClick = async () => {
        sounds.sfx.play('ok');

        const { availableDisplays } = this;
        const { visibleStats } = StateObserver.getState().ui;
        const nextIndex =
          (availableDisplays.indexOf(visibleStats) + 1) %
          availableDisplays.length;
        this.setFocusedPet(
          availableDisplays[nextIndex] === platform.playerID
            ? getPet()
            : (getFriendsPet() ?? getPet()),
        );
        attibutesEmitter.force();
      };

      const friendsNamePlate = this.getFriendsRoomPlate();
      this.friendsNamePlateText.localeText = () =>
        i18n('friendsVisit.livingRoom.plate', { friend: friendName });
      friendsNamePlate.updateOpts({
        visible: isInFriendsRoom,
      });

      attibutesEmitter.force();
    });

    createEmitter(
      this.container,
      ({ ui, user }) =>
        !!Pet.getters.getCurrentEvolution(user) && !ui.isPetCrateOpening,
    ).addListener((shouldShowNameTag) => {
      this.petName.style.visible = shouldShowNameTag;
      this.petSpecies.style.visible = shouldShowNameTag;
      this.petAge.style.visible = shouldShowNameTag;
    });

    createPersistentEmitter(shouldShowHeader).addListener((showHeader) => {
      if (showHeader) {
        this.fadeIn();
      } else {
        this.fadeOut();
      }
    });

    // Update Match2 Frenzy bar according to the current scene.
    createEmitter(this.container, ({ user }) => {
      const isMatch2Frenzy = canShowMatch2FrenzyBar(user, now);

      const isInFriendsRoom = isSceneEntered('friendsLivingRoom');
      const isInPlayableRoom =
        isSceneEntered('livingRoom') ||
        isSceneEntered('bedroom') ||
        isSceneEntered('arena') ||
        isInFriendsRoom;

      return {
        isMatch2Frenzy,
        isInFriendsRoom,
        isInPlayableRoom,
      };
    }).addListener(({ isMatch2Frenzy, isInFriendsRoom, isInPlayableRoom }) => {
      const frenzyBar = isMatch2Frenzy
        ? this.getMatch2FrenzyBar()
        : this.frenzyBar;

      frenzyBar?.updateOpts({
        visible: isMatch2Frenzy && isInPlayableRoom,
        // TODO: Temporarily solution until we decide if we want to disable Match2 during a visit
        y: isInFriendsRoom ? 325 : 200,
      });
    });
  }

  /**
   * The frenzy bar pulls ~400 kB of spritesheets on construction, so it is only
   * built once a frenzy event is actually showable.
   */
  private getMatch2FrenzyBar(): Match2FrenzyBar {
    if (this.frenzyBar) return this.frenzyBar;

    const frenzyBar = new Match2FrenzyBar({
      superview: this.container,
      width: 290,
      x: this.container.style.width * 0.5 - 150,
      y: 200,
      visible: false,
    });

    this.frenzyBar = frenzyBar;
    frenzyBar.syncState();
    if (this.active) frenzyBar.autoUpdate();

    return frenzyBar;
  }

  /**
   * Constructs the bar. Only use this to animate into it; querying visibility
   * or position must not pull the frenzy spritesheets.
   */
  public get match2FrenzyBar(): Match2FrenzyBar {
    return this.getMatch2FrenzyBar();
  }

  public isMatch2FrenzyBarVisible(): boolean {
    return this.frenzyBar?.style.visible ?? false;
  }

  /** Re-syncs the bar with state if it has been built; a no-op otherwise. */
  public refreshMatch2FrenzyBar(): void {
    this.frenzyBar?.refreshState();
  }

  public getMatch2FrenzyBarObjectivePos(
    relativeTo?: View,
  ): { x: number; y: number } | undefined {
    if (!this.frenzyBar) return undefined;

    const position = this.frenzyBar.getObjectiveIconPos(this.container);
    if (relativeTo) applyRelativeOffset(position, this.container, relativeTo);
    return {
      x: position.x,
      y: position.y,
    };
  }

  // Sets the focused pet and updates the appearance of both pets.
  private setFocusedPet(pet: PetController) {
    pet.isFocused = true;
    getPet().appearance.updateState();
    getFriendsPet()?.appearance.updateState();
  }

  private fadeIn() {
    if (this.active) return;
    this.active = true;

    this.frenzyBar?.autoUpdate();

    this.opts.superview.addSubview(this.container);
    animate(this.container)
      .clear()
      .wait(animDuration * 4)
      .now(
        { opacity: this.container.style.opacity, offsetY: -100 },
        0,
        animate.easeOut,
      )
      .then(
        { opacity: this.container.style.opacity, offsetY: -100 },
        animDuration,
        animate.easeOut,
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  private fadeOut() {
    if (!this.active) return;
    this.active = false;

    this.frenzyBar?.stopAutoUpdate();

    animate(this.container)
      .clear()
      .wait(0)
      .now(
        {
          opacity: this.container.style.opacity,
          offsetY: this.container.style.offsetY,
        },
        0,
        animate.easeOut,
      )
      .then(
        {
          opacity: this.container.style.opacity,
          offsetY: this.container.style.offsetY,
        },
        animDuration * 0.5,
        animate.easeOut,
      )
      .then({ opacity: 0, offsetY: -100 }, animDuration, animate.easeOut)
      .then(() => {
        this.opts.superview.removeSubview(this.container);
      });
  }

  private getLabelColor(id: string) {
    return id === platform.playerID ? '#AB9EA7' : '#ea6c83';
  }

  // =======================================================
  // Coins
  // =======================================================

  private createCoins(): LangBitmapFontTextView {
    const layout = new ButtonScaleView({
      tag: 'coinLayout',
      superview: this.container,
      ...uiConfig.hud.coinLayout,
      onClick: async () => {
        sounds.sfx.play('ok');

        await openCoinsShopSequence('coins_plus_button');
      },
      tracking: { feature: 'header', $subFeature: 'buy-coins' },
    });

    new ImageScaleView({
      tag: 'coinFrame',
      ...uiConfig.hud.coinFrame,
      superview: layout,
    });

    this.coinsIcon = new ImageView({
      tag: 'coinIcon',
      superview: layout,
      ...uiConfig.hud.coinIcon,
    });

    const buttonCoinsPlus = new ImageView({
      tag: 'coinPlus',
      ...uiConfig.hud.coinPlus,
      superview: layout,
    });

    const coins = new LangBitmapFontTextView({
      tag: 'coinText',
      superview: layout,
      ...uiConfig.hud.coinText,
      font: bitmapFonts(uiConfig.hud.coinText.fontName),
    });

    // Disable or enable gems button
    createPersistentEmitter(() => isBPointsButtonDisabled()).addListener(
      (bPointsButtonLocked) => {
        buttonCoinsPlus.updateOpts({ visible: !bPointsButtonLocked });
        layout.setDisabled(bPointsButtonLocked);
      },
    );

    return coins;
  }

  /**
   * Header coins icon position
   */
  public getCoinIconPos(relativeTo?: View): { x: number; y: number } {
    const position = this.coinsIcon.getPosition(this.container);
    if (relativeTo) applyRelativeOffset(position, this.container, relativeTo);
    return {
      x: position.x + this.coinsIcon.style.width / 2,
      y: position.y + this.coinsIcon.style.height / 2,
    };
  }

  /**
   * Header energy icon position
   */
  public getEnergyPos(relativeTo?: View): { x: number; y: number } {
    const position = this.energyIcon.getPosition(this.container);
    if (relativeTo) applyRelativeOffset(position, this.container, relativeTo);
    return {
      x: position.x + this.energyIcon.style.width / 2,
      y: position.y + this.energyIcon.style.height / 2,
    };
  }

  // =======================================================
  // Gems
  // =======================================================

  private createGems() {
    const layout = new ButtonScaleView({
      superview: this.container,
      ...uiConfig.hud.gemLayout,
      onClick: async () => {
        sounds.sfx.play('ok');
        // If IAP enabled just show gems shop or alternative way to get gems if IAP disabled
        await openGemsShopSequence('gem_plus_button');
      },
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ImageScaleView({
      ...uiConfig.hud.gemFrame,
      superview: layout,
    });

    this.gemsIcon = new ImageScaleView({
      ...uiConfig.hud.gemIcon,
      superview: layout,
    });

    this.gems = new LangBitmapFontTextView({
      superview: layout,
      ...uiConfig.hud.gemText,
      font: bitmapFonts(uiConfig.hud.gemText.fontName),
    });

    const buttonGemPlus = new ImageScaleView({
      ...uiConfig.hud.gemPlus,
      superview: layout,
    });

    // Disable or enable gems button
    createPersistentEmitter(() => isGemsButtonDisabled()).addListener(
      (gemsButtonLocked) => {
        buttonGemPlus.updateOpts({ visible: !gemsButtonLocked });
        layout.setDisabled(gemsButtonLocked);
      },
    );
  }

  public getGemsIconPos(relativeTo?: View): { x: number; y: number } {
    const position = this.gemsIcon.getPosition(this.container);
    if (relativeTo) applyRelativeOffset(position, this.container, relativeTo);
    return {
      x: position.x + this.gemsIcon.style.width / 2,
      y: position.y + this.gemsIcon.style.height / 2,
    };
  }

  // =======================================================
  // Energy
  // =======================================================

  private createEnergy() {
    const layout = new ButtonScaleView({
      tag: 'energyLayout',
      superview: this.container,
      ...uiConfig.hud.energyLayout,
      onClick: async () => {
        sounds.sfx.play('ok');
        await openEnergyShopSequence('energy_plus_button');
      },
      tracking: { feature: 'header', $subFeature: 'buy-energy' },
      visible: false,
    });

    this.energyLayout = layout;

    new ImageScaleView({
      tag: 'energyFrame',
      ...uiConfig.hud.energyFrame,
      superview: layout,
    });

    this.energyIcon = new ImageView({
      tag: 'energyIcon',
      superview: layout,
      ...uiConfig.hud.energyIcon,
    });

    const buttonEnergyPlus = new ImageView({
      tag: 'energyPlus',
      ...uiConfig.hud.energyPlus,
      superview: layout,
    });

    const energy = new LangBitmapFontTextView({
      tag: 'energyText',
      superview: layout,
      ...uiConfig.hud.energyText,
      font: bitmapFonts(uiConfig.hud.energyText.fontName),
    });

    this.energyRegenerationFrame = new ImageScaleView({
      superview: layout,
      ...uiConfig.hud.energyRegenerationFrame,
      visible: false,
    });

    new ImageScaleView({
      superview: this.energyRegenerationFrame,
      ...uiConfig.hud.energyRegenerationTip,
    });

    this.energyRegeneration = new LangBitmapFontTextView({
      superview: this.energyRegenerationFrame,
      ...uiConfig.hud.energyRegenerationText,
      font: bitmapFonts(uiConfig.hud.coinText.fontName),
    });

    this.energyUnlimitedTimer = new LangBitmapFontTextView({
      tag: 'energyUnlimitedTimer',
      superview: layout,
      ...uiConfig.hud.energyText,
      size: 18,
      color: '#36AE22',
      width: 50,
      x: 40,
      font: bitmapFonts(uiConfig.hud.energyText.fontName),
      visible: false,
    });

    statePromise(({ user }: State) => isMatch2Available(user)).then(() => {
      layout.updateOpts({ visible: true });
      this.updateEnergyTimer();
    });

    // Disable or enable gems button
    createPersistentEmitter(() => isEnergyButtonDisabled()).addListener(
      (energyButtonLocked) => {
        buttonEnergyPlus.updateOpts({ visible: !energyButtonLocked });
        layout.setDisabled(energyButtonLocked);
      },
    );

    return energy;
  }

  /**
   * Only built once the unlimited energy reward is actually running; the icon
   * itself is packed into `assets-init` (see assets/metadata.json).
   */
  private getEnergyUnlimitedIcon(): ImageScaleView {
    return (this.energyUnlimitedIcon ??= new ImageScaleView({
      tag: 'energyUnlimitedIcon',
      superview: this.energyLayout,
      image: 'assets/ui/match2/common/icon_infinite_green.png',
      width: 36,
      height: 36,
      x: 115,
      y: 18,
      centerAnchor: true,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    }));
  }

  private updateEnergyTimer() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();

    const time = Inventory.getters.getEnergyRegenerationConfig(user).time;
    const energy = getDisplayedEnergy();

    const timeSinceLastEnergy =
      (now - user.inventory.energyRechargeStartTime) % time;

    const timeToNextEnergy = time - timeSinceLastEnergy;

    this.energyRegenerationFrame.updateOpts({
      visible: energy < Inventory.getters.getMaxEnergy(user),
    });
    this.energyRegeneration.updateOpts({
      localeText: () =>
        i18n('header.energyRegeneration', {
          time: formatDuration(timeToNextEnergy < 0 ? 0 : timeToNextEnergy),
        }),
    });

    this.energyAnimator.setTarget(energy);

    this.updateUnlimitedEnergyTimer();

    createWaiter().wait(
      this.updateEnergyTimer.bind(this),
      duration({ seconds: 1 }),
    );
  }

  private updateUnlimitedEnergyTimer() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const unlimitedTime = getMatch2TimedRewardRemainingTime(
      user,
      'energy',
      now,
    );

    this.energyUnlimitedTimer?.updateOpts({
      visible: unlimitedTime > 0,
      localeText: () => formatDuration(unlimitedTime),
    });

    if (unlimitedTime > 0) {
      this.getEnergyUnlimitedIcon().updateOpts({ visible: true });
    } else {
      this.energyUnlimitedIcon?.updateOpts({ visible: false });
    }

    // Only show the regular energy count if the unlimited energy reward is not active
    this.energy.updateOpts({
      visible: unlimitedTime <= 0,
    });

    // Hide the energy regeneration frame if the unlimited energy reward is active.
    if (unlimitedTime > 0) {
      this.energyRegenerationFrame.updateOpts({
        visible: false,
      });
    }
  }

  // =======================================================
  // Love
  // =======================================================

  public registerMeterHandTargets(): void {
    registerHandTarget('headerStomachMeter', this.stomachMeter);
    registerHandTarget('headerLoveMeter', this.loveMeter);
  }

  private createLoveMeter() {
    const config: HudMeterOpts = {
      ...uiConfig.hud.loveMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    config.view.superview = this.container;
    this.loveMeter = new HudMeter(config);
  }

  // =======================================================
  // Stomach
  // =======================================================

  private createStomachMeter() {
    const config: HudMeterOpts = {
      ...uiConfig.hud.stomachMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    config.view.superview = this.container;
    this.stomachMeter = new HudMeter(config);
  }

  private attributes: Record<string, Pick<Attributes, 'stomach' | 'love'>> = {};

  public async tweenAttributes(
    pet: PetController,
    newAttributes: Pick<Attributes, 'stomach' | 'love'>,
    duration: number,
  ) {
    if (!this.attributes[pet.id]) {
      this.attributes[pet.id] = { stomach: 0, love: 0 };
    }

    const currentAttributes = this.attributes[pet.id];
    const { stomach, love } = pet.appearance.getAttributes();
    const { max } = Pet.ruleset.attributeBounds;
    const ds = newAttributes.stomach - stomach;
    const dl = newAttributes.love - love;
    await animate(currentAttributes)
      .clear()
      .now({ stomach, love })
      .then(
        (dt) => {
          currentAttributes.stomach = stomach + ds * dt;
          currentAttributes.love = love + dl * dt;
          if (pet.isFocused) {
            this.stomachMeter.setMeterValue(currentAttributes.stomach / max);
            this.loveMeter.setMeterValue(currentAttributes.love / max);
          }
        },
        duration,
        animate.linear,
      )
      .toPromise();
  }

  private areAttributesUpdating(userId: string) {
    const attributes = this.attributes[userId];
    return attributes && animate(attributes).hasFrames();
  }

  // =======================================================
  // Name Tag
  // =======================================================

  private createNameTag() {
    const {
      nameTagBackground,

      levelLabel,
      nameTagPetName,
      nameTagSpecies,
      nameTagAge,
    } = uiConfig.hud;

    const nameTagView = this.container.addSubview(
      new ImageCoverView(nameTagBackground),
    );

    this.levelProgress = new CircularProgressBar({
      superview: nameTagView,
      x: 101 / 2,
      y: 168 / 2 + 14,
      centerOnOrigin: true,
      progress: 0.7,
    });

    this.levelLabel = new LangBitmapFontTextView({
      ...levelLabel,
      superview: this.levelProgress,
    });

    this.petName = new LangBitmapFontTextView({
      ...nameTagPetName,
      superview: nameTagView,
    });

    this.petSpecies = new LangBitmapFontTextView({
      ...nameTagSpecies,
      superview: nameTagView,
    });

    this.petAge = new LangBitmapFontTextView({
      ...nameTagAge,
      superview: nameTagView,
    });
  }

  // =======================================================
  // Switch button
  // =======================================================
  private getSwitchButton(): ButtonScaleView {
    return (this.switchButton ??= this.createSwitchButton());
  }

  private createSwitchButton(): ButtonScaleView {
    const switchButton = new ButtonScaleView({
      tag: 'switchDisplay',
      superview: this.container,
      ...uiConfig.hud.switchLayout,
      tracking: { feature: 'header', $subFeature: 'buy-energy' },
      visible: false,
    });

    new ImageScaleView({
      superview: switchButton,
      ...uiConfig.hud.switchFrame,
    });

    new LangBitmapFontTextView({
      superview: switchButton,
      ...uiConfig.hud.switchText,
      localeText: () => i18n('friendsVisit.switch'),
    });

    return switchButton;
  }

  // =======================================================
  // Friend's name plate
  // =======================================================

  private getFriendsRoomPlate(): ImageScaleView {
    return (this.friendsNamePlate ??= this.createFriendsRoomPlate());
  }

  private createFriendsRoomPlate(): ImageScaleView {
    const friendsNamePlate = new ImageScaleView({
      superview: this.container,
      image: 'assets/ui/visit/frames/friend_plate.png',
      x: this.container.style.width * 0.5,
      y: 240,
      width: 338,
      height: 150,
      centerOnOrigin: true,
    });

    this.friendsNamePlateText = new LangBitmapFontTextView({
      superview: friendsNamePlate,
      x: friendsNamePlate.style.width * 0.5,
      y: 100,
      width: 307,
      height: 47,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: '#fff',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () =>
        i18n('friendsVisit.livingRoom.plate', { friend: 'Tama' }),
      centerOnOrigin: true,
    });

    return friendsNamePlate;
  }
}
